import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import './frontend.css';

const PillNavAnimations = ({ container }) => {
	const [currentSlide, setCurrentSlide] = useState(1);
	const [isAutoToggling, setIsAutoToggling] = useState(true);
	const autoToggleRef = useRef(null);
	
	// Get configuration from data attributes
	const initialState = container.dataset.initialState || 'split';
	const autoToggle = container.dataset.autoToggle === 'true';
	const toggleInterval = parseFloat(container.dataset.toggleInterval) || 5;
	const onToggleAction = container.dataset.onToggleAction || 'none';
	const targetSection = container.dataset.targetSection || '#contact';
	const redirectUrl = container.dataset.redirectUrl || '';

	// Initialize current slide based on initial state
	useEffect(() => {
		const initialSlide = initialState === 'center' ? 2 : 1;
		setCurrentSlide(initialSlide);
		
		// Update pill toggle state
		const pillToggle = container.querySelector('.pill-toggle');
		if (pillToggle) {
			pillToggle.setAttribute('data-current-slide', initialSlide.toString());
		}
	}, [initialState]);

	// Slide switching function
	const switchSlide = (slideNumber) => {
		const pillToggle = container.querySelector('.pill-toggle');
		
		console.log('Pill Nav: Switching to slide:', slideNumber);
		
		// Update pill toggle state
		if (pillToggle) {
			pillToggle.setAttribute('data-current-slide', slideNumber.toString());
		}
		
		// Update current slide state
		setCurrentSlide(slideNumber);
		
		// Handle toggle actions
		handleToggleAction(slideNumber);
	};

	// Handle toggle actions
	const handleToggleAction = (slideNumber) => {
		switch (onToggleAction) {
			case 'scroll-to-section':
				if (slideNumber === 1 && targetSection) {
					// Small delay to ensure transition completes
					setTimeout(() => {
						const element = document.querySelector(targetSection);
						if (element) {
							element.scrollIntoView({ 
								behavior: 'smooth',
								block: 'start'
							});
						}
					}, 300);
				}
				break;
			case 'redirect':
				if (slideNumber === 1 && redirectUrl) {
					setTimeout(() => {
						window.location.href = redirectUrl;
					}, 300);
				}
				break;
			case 'none':
			default:
				// No action
				break;
		}
	};

	// Auto toggle functionality
	useEffect(() => {
		if (autoToggle && isAutoToggling) {
			autoToggleRef.current = setInterval(() => {
				setCurrentSlide(prev => {
					const nextSlide = prev === 1 ? 2 : 1;
					switchSlide(nextSlide);
					return nextSlide;
				});
			}, toggleInterval * 1000);
		}

		return () => {
			if (autoToggleRef.current) {
				clearInterval(autoToggleRef.current);
			}
		};
	}, [autoToggle, isAutoToggling, toggleInterval]);

	// Add pill navigation event listeners
	useEffect(() => {
		// Pill toggle event listener
		const pillToggle = container.querySelector('.pill-toggle');
		
		const handlePillClick = (e) => {
			e.preventDefault();
			e.stopPropagation();
			const nextSlide = currentSlide === 1 ? 2 : 1;
			setIsAutoToggling(false); // Stop auto-toggling when user interacts
			switchSlide(nextSlide);
		};
		
		if (pillToggle) {
			// Remove any existing listeners first
			pillToggle.removeEventListener('click', handlePillClick);
			pillToggle.addEventListener('click', handlePillClick);
			
			// Ensure the pill toggle has the correct initial state
			pillToggle.setAttribute('data-current-slide', currentSlide.toString());
		}
		
		// Add hover events to pause auto-toggling
		const handleMouseEnter = () => {
			setIsAutoToggling(false);
		};
		
		const handleMouseLeave = () => {
			if (autoToggle) {
				setIsAutoToggling(true);
			}
		};
		
		container.addEventListener('mouseenter', handleMouseEnter);
		container.addEventListener('mouseleave', handleMouseLeave);
		
		// Cleanup
		return () => {
			if (pillToggle) {
				pillToggle.removeEventListener('click', handlePillClick);
			}
			container.removeEventListener('mouseenter', handleMouseEnter);
			container.removeEventListener('mouseleave', handleMouseLeave);
		};
	}, [autoToggle, currentSlide]);

	// Intersection Observer for performance optimization
	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						// Element is visible, resume auto-toggling if enabled
						if (autoToggle) {
							setIsAutoToggling(true);
						}
					} else {
						// Element is not visible, pause auto-toggling
						setIsAutoToggling(false);
					}
				});
			},
			{ threshold: 0.1 }
		);

		observer.observe(container);

		return () => {
			observer.disconnect();
		};
	}, [autoToggle]);

	// This component doesn't render anything - it just manages animations
	return null;
};

// Initialize React components for all pill navigation containers
const initPillNavBlocks = () => {
	const containers = document.querySelectorAll('.pill-nav-container');
	
	containers.forEach(container => {
		// Skip if already initialized
		if (container.dataset.reactInitialized === 'true') return;
		
		// Create a wrapper div for React
		const reactWrapper = document.createElement('div');
		reactWrapper.style.display = 'contents'; // Don't affect layout
		container.appendChild(reactWrapper);
		
		// Create React root and render
		const root = createRoot(reactWrapper);
		root.render(<PillNavAnimations container={container} />);
		
		// Mark as initialized
		container.dataset.reactInitialized = 'true';
	});
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initPillNavBlocks);
} else {
	initPillNavBlocks();
}

// Re-initialize on dynamic content changes (for WordPress)
if (typeof MutationObserver !== 'undefined') {
	const observer = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			if (mutation.type === 'childList') {
				initPillNavBlocks();
			}
		});
	});
	
	observer.observe(document.body, {
		childList: true,
		subtree: true
	});
}
